import { Inline, Stack, Text } from '@front10/base-elements';
import produce from 'immer';
import * as React from 'react';
import { count } from '../utils';
import { Layout, PeopleCounterItem } from './PeopleCounterItem';
import { PeopleAgePicker as PeopleAgePickerSelect } from './PeopleAgePickerSelect';
import { PeopleAgePicker } from './PeopleAgePicker';
import { useEffectReducer } from 'use-effect-reducer';
import { evaluateRules } from './rulesParser';

export interface PassengerProps {
  name: string;
  label: string;
  description: string;
  minCount?: number;
  maxCount?: number;
  shouldDetailAges: boolean;
  minAge: number;
  maxAge: number;
  rules?: Array<{}>;
  show?: boolean;
  showDetailedAgeOptions?: boolean;
}

export interface PassengersProps {
  options: Array<PassengerProps>;
  defaultSelection: Record<string, number>;
  totalTravelers: number;
  totalTravelersPerRoomExcludeInfants: boolean;
}

interface Props {
  title?: string;
  options: PassengersProps['options'];
  layout?: Layout;
  origin?: string;
  destination?: string;
  preventBottomDisplacement?: boolean;
  updateFocusAction?: () => void;
  popoverRef?: any;
}

function showOption(show: any) {
  return show === undefined || show ? true : false;
}

type UsePeopleCounterProps = ReturnType<typeof usePeopleCounter>;

export function PeopleCounter(
  props: Props &
    Omit<UsePeopleCounterProps, 'count' | 'isEventValid'> &
    Partial<Pick<UsePeopleCounterProps, 'isEventValid'>>
) {
  const options = props.options ?? [];
  const value = props.value ?? [1];
  const dispatch = props.dispatch;
  const layout = props.layout ?? 'block';
  const isValid = props.isEventValid;
  const PeopleAgePickerCmp = props.preventBottomDisplacement
    ? PeopleAgePickerSelect
    : PeopleAgePicker;

  const content = (
    <>
      {props.title && (
        <Text variant="fontSize4" sx={{ color: 'text', pb: 3, width: '100%' }}>
          {props.title}
        </Text>
      )}
      {options.map((config, index) => {
        const addEvent = { index, type: 'ADD' };
        const removeEvent = { index, type: 'REMOVE' };

        return (
          showOption(config.show) && (
            <React.Fragment key={config.name}>
              <PeopleCounterItem
                onAdd={() => dispatch(addEvent)}
                onRemove={() => dispatch(removeEvent)}
                addButtonDisabled={isValid ? !isValid(addEvent, value) : false}
                removeButtonDisabled={isValid ? !isValid(removeEvent, value) : false}
                config={config}
                count={value.count[index]}
                layout={layout}
              />
              {layout === 'block' ? (
                <PeopleAgePickerCmp
                  config={config}
                  ages={value.ages[index]}
                  onChange={(ageIndex, value) =>
                    dispatch({
                      type: 'AGE_CHANGE',
                      typeIndex: index,
                      instanceIndex: ageIndex,
                      value
                    })
                  }
                  updateFocusAction={props?.updateFocusAction}
                  popoverRef={props?.popoverRef}
                />
              ) : null}
            </React.Fragment>
          )
        );
      })}
    </>
  );

  if (layout === 'inline') {
    return (
      <React.Fragment>
        <Inline gap={1} align="space-between">
          {content}
        </Inline>
        {options.map((config, index) => (
          <PeopleAgePickerCmp
            key={config.name}
            config={config}
            ages={value.ages[index]}
            onChange={(ageIndex, value) =>
              dispatch({
                type: 'AGE_CHANGE',
                typeIndex: index,
                instanceIndex: ageIndex,
                value
              })
            }
            updateFocusAction={props?.updateFocusAction}
            popoverRef={props?.popoverRef}
          />
        ))}
      </React.Fragment>
    );
  }

  return <Stack gap={1}>{content}</Stack>;
}

export function getPeopleObject(passengerOptions: PassengersProps['options'], info: PeopleInfo) {
  return [
    Object.fromEntries(
      passengerOptions.map((passenger, index) => [passenger.name, info.count[index]])
    ),
    info.ages
      ? Object.fromEntries(
          passengerOptions.map((passenger, index) => [passenger.name, info.ages[index]])
        )
      : []
  ];
}

export type PeopleInfo = {
  count: number[];
  ages: number[][];
};

type InitialValue = PeopleInfo | Record<string, number>;

function isPeopleInfo(value: InitialValue): value is PeopleInfo {
  return Array.isArray((value as PeopleInfo).count);
}

export function useDefaultPeopleSelection(
  options: PassengersProps['options'],
  defaultSelection: InitialValue
) {
  return React.useMemo(() => {
    if (isPeopleInfo(defaultSelection)) {
      return defaultSelection;
    }
    return {
      count: options.map((v) => defaultSelection[v.name] ?? 0),
      ages: options.map((v) =>
        Array((v.shouldDetailAges && defaultSelection[v.name]) || 0).fill(v.minAge)
      )
    };
  }, [defaultSelection, options]);
}

export function usePeopleCounter(
  passengers: Omit<PassengersProps, 'defaultSelection'> & {
    defaultSelection: InitialValue;
  },
  onChange?: (value: PeopleInfo) => void,
  rules: Array<string> = []
) {
  const maxCount = passengers.options.map((v) => v.maxCount);
  const minCount = passengers.options.map((v) => v.minCount ?? 0);
  const initialState = useDefaultPeopleSelection(passengers.options, passengers.defaultSelection);
  const excludeChildren = passengers?.totalTravelersPerRoomExcludeInfants;
  //the value to get Infant index is hardcoded because not should change in a future
  const infantIndex = passengers?.totalTravelersPerRoomExcludeInfants
    ? passengers.options.findIndex((item) => item.name === 'age3')
    : -1;

  const evaluator = evaluateRules(rules, passengers);

  const [state, dispatch] = useEffectReducer((prevState, event, exec) => {
    const newState = produce(prevState, (draft) => {
      const { ruleActions } = evaluator(prevState, event.type, event.index);
      switch (event.type) {
        case 'ADD': {
          const index = event.index;
          if (isEventValid(event, prevState)) {
            draft.count[index] += 1;
            draft.ages[index].push(passengers.options[index].minAge);
          }
          break;
        }
        case 'REMOVE': {
          const index = event.index;
          if (isEventValid(event, prevState)) {
            draft.count[index] -= 1;
            draft.ages[index].pop();
            ruleActions(draft);
          }
          break;
        }
        case 'AGE_CHANGE': {
          const typeIndex = event.typeIndex;
          const instanceIndex = event.instanceIndex;
          draft.ages[typeIndex][instanceIndex] = event.value;
          break;
        }
        case 'VALUE_SET': {
          return event.value;
        }
      }
    });
    exec(() => onChange?.(newState));
    return newState;
  }, initialState);

  function isEventValid(event: any, currentState: typeof state) {
    const totalCount = excludeChildren
      ? count(currentState.count.filter((item, index) => index !== infantIndex))
      : count(currentState.count);

    const canAddChild =
      excludeChildren && infantIndex === event.index
        ? true
        : totalCount < passengers.totalTravelers;

    const { valid } = evaluator(currentState, event.type, event.index);
    switch (event.type) {
      case 'ADD': {
        const index = event.index;

        return (
          valid &&
          canAddChild &&
          (maxCount[index] === undefined || currentState.count[index] < (maxCount[index] as number))
        );
      }
      case 'REMOVE': {
        const index = event.index;
        return currentState.count[index] > minCount[index];
      }
      default: {
        return true;
      }
    }
  }

  return { value: state, dispatch, count: count(state.count), isEventValid };
}
